import {
  FranchiseeItemFragment,
  TeacherRole,
  TeacherTag,
} from "../gql/graphql.ts";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { BasicTable } from "../UI/Table/BasicTable.tsx";
import { TeacherTableDotDropdown } from "./TeacherTableDotDropdown.tsx";
import { useToTeacherDetail } from "./useToTeacherDetail.ts";
import { TeacherScoreWithId } from "./TeacherCoordinatorReview/useTeacherScores.ts";
import { MouseEvent, useMemo } from "react";
import { useCopyToClipboard } from "../UI/Feedback/useCopyToClipboard.ts";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import {
  SortDirection,
  SortLabels,
  TableColumnHeader,
} from "../UI/Table/TableColumnHeader.tsx";
import { NumberRangeFilter } from "../UI/Table/Filters/NumberRangeFilter.tsx";
import { isEmptyRange } from "../UI/Table/numberRange.ts";
import { compareAlphabetically } from "../UI/Table/compareAlphabetically.ts";
import {
  TeacherTableFilters,
  TeacherTableSort,
  TeacherTableSortField,
} from "./useTeacherTableUrlState.ts";

const ALPHABETICAL_SORT_LABELS: SortLabels = { asc: "A → Z", desc: "Z → A" };
const NUMERIC_SORT_LABELS: SortLabels = {
  asc: "Lowest first",
  desc: "Highest first",
};

function withoutTriggeringRowClick(action: () => void) {
  return (event: MouseEvent) => {
    event.stopPropagation();
    action();
  };
}

function FinalScoreBadge({
  score,
  teacherId,
}: {
  score: TeacherScoreWithId | undefined;
  teacherId: string;
}) {
  const { toTeacherDetail } = useToTeacherDetail();
  const handleClick = withoutTriggeringRowClick(() =>
    toTeacherDetail(teacherId),
  );

  if (!score) {
    return (
      <button
        onClick={handleClick}
        className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-400 hover:bg-slate-200 transition-colors"
      >
        —
      </button>
    );
  }

  const v = score.finalScore;
  const color =
    v >= 4
      ? "bg-green-100 text-green-800 hover:bg-green-200"
      : v >= 3
        ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
        : "bg-red-100 text-red-800 hover:bg-red-200";

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors ${color}`}
    >
      {v.toFixed(2)}
    </button>
  );
}

export function FranchiseeTable(props: {
  franchisees: FranchiseeItemFragment[];
  editFranchisee: (franchisee: FranchiseeItemFragment) => void;
  internalIds: Set<string>;
  teachersWithMissingReviewIds?: Set<string>;
  scoreByTeacherId?: Map<string, TeacherScoreWithId>;
  newTeacherIds?: Set<string>;
  reviewCountByTeacherId?: Map<string, number>;
  sort: TeacherTableSort;
  onSortChange: (sort: TeacherTableSort) => void;
  filters: TeacherTableFilters;
  onFiltersChange: (filters: TeacherTableFilters) => void;
}) {
  const franchisees = props.franchisees;
  const { toTeacherDetail } = useToTeacherDetail();
  const copyToClipboard = useCopyToClipboard();
  const columnHelper = createColumnHelper<FranchiseeItemFragment>();

  const columns = useMemo(() => {
    const sortableBy = (
      field: TeacherTableSortField,
      sortLabels: SortLabels,
    ) => ({
      sortLabels,
      sortDirection:
        props.sort.sortBy === field ? props.sort.sortDirection : undefined,
      onSort: (sortDirection: SortDirection) =>
        props.onSortChange({ sortBy: field, sortDirection }),
    });

    const filterableByRange = (
      field: keyof TeacherTableFilters,
      allowDecimals: boolean,
    ) => ({
      isFiltered: !isEmptyRange(props.filters[field]),
      renderFilter: (close: () => void) => (
        <NumberRangeFilter
          value={props.filters[field]}
          allowDecimals={allowDecimals}
          close={close}
          onApply={(range) =>
            props.onFiltersChange({ ...props.filters, [field]: range })
          }
        />
      ),
    });

    return [
      columnHelper.accessor(
        (teacher) => `${teacher.givenName} ${teacher.familyName}`,
        {
          id: "name",
          sortingFn: compareAlphabetically,
          header: () => (
            <TableColumnHeader
              label="Teacher"
              description="Name and email"
              {...sortableBy("name", ALPHABETICAL_SORT_LABELS)}
            />
          ),
          cell: (info) => (
            <div className="min-w-0">
              <span className="flex items-center gap-2 min-w-0">
                <span className="min-w-0 truncate font-medium text-slate-900">
                  {info.getValue()}
                </span>
                {props.internalIds.has(info.row.original.id) && (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                    Internal
                  </span>
                )}
                {info.row.original.roles.includes(TeacherRole.NormaStaff) && (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                    Staff Norma
                  </span>
                )}
                {info.row.original.tags.includes(TeacherTag.OnboardingLive) && (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-medium text-fuchsia-700">
                    Live Onboarding
                  </span>
                )}
                {props.newTeacherIds?.has(info.row.original.id) && (
                  <span className="inline-flex shrink-0 items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                    New
                  </span>
                )}
                {props.teachersWithMissingReviewIds?.has(
                  info.row.original.id,
                ) && (
                  <button
                    onClick={withoutTriggeringRowClick(() =>
                      toTeacherDetail(info.row.original.id),
                    )}
                    className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-200 transition-colors"
                  >
                    Review needed
                  </button>
                )}
              </span>
              <span className="flex items-center gap-1 group min-w-0">
                <span
                  className="min-w-0 truncate text-xs text-slate-500"
                  title={info.row.original.email}
                >
                  {info.row.original.email}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(info.row.original.email, "Email copied");
                  }}
                  className="shrink-0 invisible group-hover:visible p-1 rounded-full hover:bg-slate-100"
                  title="Copy email"
                >
                  <ClipboardIcon className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </span>
            </div>
          ),
        },
      ),
      columnHelper.accessor(
        (teacher) =>
          teacher.phoneNumber
            ? `${teacher.phoneNumber.countryCode} ${teacher.phoneNumber.number}`
            : undefined,
        {
          id: "phoneNumber",
          meta: { className: "hidden md:table-cell md:w-[210px]" },
          sortingFn: compareAlphabetically,
          sortUndefined: "last",
          header: () => (
            <TableColumnHeader
              label="Phone number"
              {...sortableBy("phoneNumber", ALPHABETICAL_SORT_LABELS)}
            />
          ),
          cell: (info) => {
            const phoneText = info.getValue();
            if (!phoneText) return "";
            return (
              <span className="flex items-center gap-1 group min-w-0">
                <span className="truncate">{phoneText}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(phoneText, "Phone number copied");
                  }}
                  className="shrink-0 invisible group-hover:visible p-1 rounded-full hover:bg-slate-100 shadow-sm"
                  title="Copy phone"
                >
                  <ClipboardIcon className="w-4 h-4 text-slate-400" />
                </button>
              </span>
            );
          },
        },
      ),
      columnHelper.accessor(
        (teacher) => props.scoreByTeacherId?.get(teacher.id)?.finalScore,
        {
          id: "finalScore",
          meta: { className: "w-28 md:w-[130px]" },
          sortingFn: "basic",
          sortUndefined: "last",
          header: () => (
            <TableColumnHeader
              label="Score"
              {...sortableBy("finalScore", NUMERIC_SORT_LABELS)}
              {...filterableByRange("finalScore", true)}
            />
          ),
          cell: (info) => (
            <FinalScoreBadge
              score={props.scoreByTeacherId?.get(info.row.original.id)}
              teacherId={info.row.original.id}
            />
          ),
        },
      ),
      columnHelper.accessor(
        (teacher) => props.reviewCountByTeacherId?.get(teacher.id),
        {
          id: "reviewCount",
          meta: { className: "hidden md:table-cell md:w-[140px]" },
          sortingFn: "basic",
          sortUndefined: "last",
          header: () => (
            <TableColumnHeader
              label="Reviews"
              {...sortableBy("reviewCount", NUMERIC_SORT_LABELS)}
              {...filterableByRange("reviewCount", false)}
            />
          ),
          cell: (info) => {
            const count = info.getValue();
            if (count === undefined) return null;
            return (
              <button
                onClick={withoutTriggeringRowClick(() =>
                  toTeacherDetail(info.row.original.id),
                )}
                className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-200 transition-colors"
              >
                {count}
              </button>
            );
          },
        },
      ),
      columnHelper.display({
        id: "actions",
        meta: { className: "w-12 md:w-[70px]" },
        cell: (v) => (
          <div onClick={(event) => event.stopPropagation()}>
            <TeacherTableDotDropdown
              authId={v.row.original.authId}
              teacherId={v.row.original.id}
            />
          </div>
        ),
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.internalIds,
    props.teachersWithMissingReviewIds,
    props.scoreByTeacherId,
    props.newTeacherIds,
    props.reviewCountByTeacherId,
    props.sort,
    props.onSortChange,
    props.filters,
    props.onFiltersChange,
    toTeacherDetail,
    copyToClipboard,
  ]);

  const sorting = useMemo(
    () => [
      { id: props.sort.sortBy, desc: props.sort.sortDirection === "desc" },
    ],
    [props.sort],
  );

  const table = useReactTable({
    columns,
    data: franchisees,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <BasicTable
        table={table}
        tableFixed
        scrollableMinWidthClassName={"md:min-w-[46rem]"}
        onRowClick={(teacher) => toTeacherDetail(teacher.id)}
      />
      {franchisees.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-400">
          No teachers match the current filters.
        </p>
      )}
    </>
  );
}
